"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  DatamuseCredit,
  ExampleWords,
  LookupState,
  revealField,
  WordChips,
} from "@/components/tools/vocabulary/word-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { normalizeLookup } from "@/lib/tool-kit/vocabulary/datamuse";
import {
  type DescribeMode,
  type DescribeSort,
  describingQuery,
  describingWords,
  modeOfQuery,
  pairing,
  wordOfQuery,
} from "@/lib/tool-kit/vocabulary/describing-words";
import { useDatamuse } from "@/lib/tool-kit/vocabulary/use-datamuse";

/** English strings. A translated content file overrides these through `ui`. */
export const DESCRIBING_WORDS_FINDER_STRINGS = {
  modeLabel: "Find",
  modes: {
    adjectives: "Adjectives for a noun",
    nouns: "Nouns for an adjective",
  } as Record<DescribeMode, string>,
  wordLabels: {
    adjectives: "Noun",
    nouns: "Adjective",
  } as Record<DescribeMode, string>,
  placeholders: {
    adjectives: "evidence",
    nouns: "significant",
  } as Record<DescribeMode, string>,
  wordHint:
    "Results appear as you type. Click a pairing to explore from its other word.",
  sortLabel: "Order",
  sorts: {
    common: "Most often used together",
    alphabetical: "Alphabetical",
  } as Record<DescribeSort, string>,
  tryLabel: "Try:",
  examples: {
    adjectives: ["evidence", "argument", "research", "results"],
    nouns: ["significant", "empirical", "critical", "robust"],
  } as Record<DescribeMode, string[]>,
  emptyPrompts: {
    adjectives:
      "Type a noun to see the adjectives writers most often put in front of it.",
    nouns: "Type an adjective to see the nouns it most often describes.",
  } as Record<DescribeMode, string>,
  headings: {
    adjectives: "Adjectives used with “{word}”",
    nouns: "Nouns described by “{word}”",
  } as Record<DescribeMode, string>,
  pairCount: "one:{count} pairing|other:{count} pairings",
  orderHints: {
    common:
      "Most frequent pairings first, counted in Google Books. Frequent is not the same as precise — choose the one that says what you mean.",
    alphabetical: "In alphabetical order.",
  } as Record<DescribeSort, string>,
  noResults: {
    adjectives:
      "No adjectives found for “{word}”. Check the spelling and that it is a noun — evidence rather than evident.",
    nouns:
      "No nouns found for “{word}”. Check the spelling and that it is an adjective — significant rather than significance.",
  } as Record<DescribeMode, string>,
  copyList: "Copy list",
  copied: "Copied!",
  searching: "Searching…",
  errorNetwork:
    "Could not reach the Datamuse word service. Check your connection and try again.",
  errorBusy:
    "Datamuse is receiving too many requests right now. Wait a minute and try again.",
  errorUnavailable:
    "The Datamuse word service did not answer. Try again in a moment.",
  retry: "Try again",
  credit: "Word data from the Datamuse API",
} as const;

type Strings = typeof DESCRIBING_WORDS_FINDER_STRINGS;

const FIELD_ID = "describe-word";
const MAX_INPUT = 40;
const RE_LETTER = /\p{L}/u;
const MODES: DescribeMode[] = ["adjectives", "nouns"];
const SORTS: DescribeSort[] = ["common", "alphabetical"];

function Results({
  word,
  mode,
  words,
  sort,
  onSelect,
  stale,
  strings,
}: {
  word: string;
  mode: DescribeMode;
  words: readonly string[];
  sort: DescribeSort;
  onSelect: (word: string) => void;
  stale: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (words.length === 0) {
    return <Notice>{t(strings.noResults[mode], { word })}</Notice>;
  }
  return (
    <div className={stale ? "space-y-4 opacity-60" : "space-y-4"}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div aria-live="polite" className="space-y-1">
          <h2 className={WIDGET_H2}>{t(strings.headings[mode], { word })}</h2>
          <p className={HINT}>
            {tPlural(strings.pairCount, locale, words.length, {
              count: fmt(words.length),
            })}
          </p>
        </div>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyList}
          small
          text={words.join(", ")}
        />
      </div>
      <p className={HINT}>{strings.orderHints[sort]}</p>
      <WordChips
        format={(result) => pairing(word, result, mode)}
        onSelect={onSelect}
        words={words}
      />
    </div>
  );
}

export default function DescribingWordsFinderWidget() {
  const strings = useToolUi(DESCRIBING_WORDS_FINDER_STRINGS);
  const [mode, setMode] = useState<DescribeMode>("adjectives");
  const [sort, setSort] = useState<DescribeSort>("common");
  const [input, setInput] = useState("");
  const normalized = normalizeLookup(input);
  const word = RE_LETTER.test(normalized) ? normalized : "";
  const lookup = useDatamuse(word ? [describingQuery(word, mode)] : []);
  const answeredMode = modeOfQuery(lookup.answered);
  const answeredWord = wordOfQuery(lookup.answered);
  const answer = word ? lookup.results[0] : undefined;

  function lookUp(next: string) {
    setInput(next);
    revealField(FIELD_ID);
  }

  /** A clicked pairing looks up its other word, from the other side. */
  function explore(result: string) {
    setMode(answeredMode === "adjectives" ? "nouns" : "adjectives");
    lookUp(result);
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="describe-mode"
        onChange={setMode}
        options={MODES.map((value) => ({ value, label: strings.modes[value] }))}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
        <TextField
          hint={strings.wordHint}
          id={FIELD_ID}
          label={strings.wordLabels[mode]}
          onChange={(value) => setInput(value.slice(0, MAX_INPUT))}
          placeholder={strings.placeholders[mode]}
          type="search"
          value={input}
        />
        <SelectField
          id="describe-sort"
          label={strings.sortLabel}
          onChange={setSort}
          options={SORTS.map((value) => ({
            value,
            label: strings.sorts[value],
          }))}
          value={sort}
        />
      </div>
      {word ? (
        <LookupState
          failure={lookup.failure}
          onRetry={lookup.retry}
          status={lookup.status}
          strings={strings}
        />
      ) : (
        <div className="space-y-3">
          <Notice>{strings.emptyPrompts[mode]}</Notice>
          <ExampleWords
            label={strings.tryLabel}
            onSelect={lookUp}
            words={strings.examples[mode]}
          />
        </div>
      )}
      {answer && answeredMode ? (
        <Results
          mode={answeredMode}
          onSelect={explore}
          sort={sort}
          stale={lookup.status === "loading"}
          strings={strings}
          word={answeredWord}
          words={describingWords(answeredWord, answer, sort)}
        />
      ) : null}
      <DatamuseCredit label={strings.credit} />
    </>
  );
}
